"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@workspace/ui/components/button";
import { Plus, LayoutTemplate, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { DocumentList } from "@/components/documents/document-list";
import { CollectionList } from "@/components/collections/collection-list";
import type { FilterId } from "@/components/collections/collection-list";
import { ImportDialog } from "@/components/import/import-dialog";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { apiClient } from "@/lib/api-client";
import { useMounted } from "@/lib/use-mounted";

export default function DocsHomePage() {
  const router = useRouter();
  const { loading } = useAuth({ requireAuth: true });
  const mounted = useMounted();
  const [importOpen, setImportOpen] = useState(false);
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [creating, setCreating] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterId>("__all");

  const createDocument = async () => {
    const result = await apiClient.safeRequest<{ id: string }>(
      "/api/documents",
      { method: "POST", body: { title: "Untitled" } },
    );
    if (result.ok) {
      router.push(`/${result.data.id}`);
    }
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) return;
    setCreating(true);
    try {
      const result = await apiClient.safeRequest("/api/collections", {
        method: "POST",
        body: { name: newCollectionName.trim() },
      });
      if (result.ok) {
        setNewCollectionOpen(false);
        setNewCollectionName("");
      }
    } finally {
      setCreating(false);
    }
  };

  if (!mounted || loading) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-12 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="col-span-12 md:col-span-6 lg:col-span-4 h-24 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Documents</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Import
          </Button>
          <Button
            variant="outline"
            onClick={() => router.push("/templates")}
            className="gap-2"
          >
            <LayoutTemplate className="h-4 w-4" />
            Templates
          </Button>
          <Button onClick={createDocument} className="gap-2">
            <Plus className="h-4 w-4" />
            New Document
          </Button>
        </div>
      </div>

      <CollectionList
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        onNewCollection={() => setNewCollectionOpen(true)}
      />

      <DocumentList filter={activeFilter} />

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />

      <Dialog open={newCollectionOpen} onOpenChange={setNewCollectionOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Collection</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Collection name"
            value={newCollectionName}
            onChange={(e) => setNewCollectionName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateCollection();
            }}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewCollectionOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCollection}
              disabled={!newCollectionName.trim() || creating}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
